import { Button, Input, Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { endOfMonth, startOfMonth } from "date-fns";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { CommunicationCalendarItemDTO } from "@/modules/communication/domain/contracts";
import { formatDateForInput, formatTimeLocal } from "@/shared/lib/date-time";
import type { TimetableItem } from "@/shared/ui/timetable";
import { PublicTimetable } from "@/shared/ui/timetable";
import { useCommunicationCalendarQuery } from "../api/queries";
import { CommunicationTour } from "../tour/CommunicationTour";
import { calendarTourSteps } from "../tour/communicationTourSteps";
import { useCommunicationTour } from "../tour/useCommunicationTour";
import { CalendarAgendaRow } from "../ui/CalendarAgendaRow";

const LINKED_ITEM_START = "08:00";
const LINKED_ITEM_END = "08:30";

/**
 * ISO จาก API → วัน yyyy-MM-dd ตาม timezone ของ browser (ใช้กับ cell ปฏิทิน)
 * — สอดคล้อง formatDateForInput ใน date-time.ts
 */
function isoToLocalYmd(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10) || null;
  return formatDateForInput(d);
}

/** ISO → HH:mm ตาม timezone ของ browser */
function isoToLocalHm(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const m = iso.match(/T(\d{2}:\d{2})/);
    return m?.[1] ?? null;
  }
  const t = formatTimeLocal(d);
  return t || null;
}

function sortKeyForCalendarItem(item: CommunicationCalendarItemDTO): string {
  if (item.type === "event") return item.startAt ?? "";
  return item.dueDate ?? "";
}

function formatAgendaWhen(item: CommunicationCalendarItemDTO): string {
  try {
    if (item.type === "event" && item.startAt) {
      const start = new Date(item.startAt);
      const startYmd = isoToLocalYmd(item.startAt);
      const endYmd = item.endAt ? isoToLocalYmd(item.endAt) : null;
      const dateStr = start.toLocaleDateString("lo-LA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const startT = isoToLocalHm(item.startAt) ?? "";
      if (item.endAt && endYmd && startYmd && endYmd !== startYmd) {
        const end = new Date(item.endAt);
        const endStr = end.toLocaleDateString("lo-LA", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        return `${dateStr} → ${endStr}`;
      }
      const endT = isoToLocalHm(item.endAt);
      if (startT && endT) return `${dateStr} · ${startT}–${endT}`;
      if (startT) return `${dateStr} · ${startT}`;
      return dateStr;
    }
    if (item.type === "task" && item.dueDate) {
      const d = new Date(item.dueDate);
      return d.toLocaleDateString("lo-LA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  } catch {
    /* fall through */
  }
  return item.startAt ?? item.dueDate ?? "—";
}

const CALENDAR_SOURCE_LABEL: Record<string, string> = {
  "calendar-event": "ນັດຫມາຍ (ປະຕິທິນ)",
  meeting: "ກອງປະຊຸມ",
  "meeting-action": "ວຽກຈາກກອງປະຊຸມ",
  "project-task": "ວຽກໂຄງການ",
};

function kindLabel(item: CommunicationCalendarItemDTO): string {
  return (
    CALENDAR_SOURCE_LABEL[item.source] ??
    (item.type === "task" ? "ວຽກມອບຫມາຍ" : "ກິດຈະກຳ")
  );
}

function communicationCalendarToTimetableItem(item: {
  id: string;
  type: "event" | "task";
  title: string;
  description: string | null;
  startAt: string | null;
  endAt: string | null;
  dueDate: string | null;
}): TimetableItem | null {
  if (item.type === "event") {
    const date = isoToLocalYmd(item.startAt);
    const startTime = isoToLocalHm(item.startAt);
    if (!date) return null;
    return {
      id: `event-${item.id}`,
      date,
      startTime: startTime ?? LINKED_ITEM_START,
      endTime: isoToLocalHm(item.endAt) ?? LINKED_ITEM_END,
      title: item.title,
      description: item.description ?? undefined,
      type: "event",
    };
  }

  const date = isoToLocalYmd(item.dueDate);
  if (!date) return null;
  return {
    id: `task-${item.id}`,
    date,
    startTime: LINKED_ITEM_START,
    endTime: LINKED_ITEM_END,
    title: item.title,
    description: item.description ?? undefined,
    type: "project_task",
  };
}

export function CommunicationCalendarPage() {
  const tour = useCommunicationTour("communication-calendar-tour-completed");
  const navigate = useNavigate({ from: "/app/communication/calendar" });
  const searchParams = useSearch({
    from: "/app/communication/calendar",
  }) as { tab?: "calendar" | "list" };
  const tab = searchParams.tab === "list" ? "list" : "calendar";

  const [rangeStart, setRangeStart] = useState(() =>
    formatDateForInput(startOfMonth(new Date())),
  );
  const [rangeEnd, setRangeEnd] = useState(() =>
    formatDateForInput(endOfMonth(new Date())),
  );
  const [monthCursor, setMonthCursor] = useState(() =>
    startOfMonth(new Date()),
  );
  const [calendarMountKey, setCalendarMountKey] = useState(0);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useCommunicationCalendarQuery({
    fromDate: rangeStart,
    toDate: rangeEnd,
  });
  const items = data?.data ?? [];
  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!normalizedSearch) return items;
    return items.filter((item) =>
      [item.title, item.description ?? "", item.source, item.status ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [items, normalizedSearch]);

  const sortedListItems = useMemo(() => {
    return [...filteredItems].sort((a, b) =>
      sortKeyForCalendarItem(b).localeCompare(sortKeyForCalendarItem(a)),
    );
  }, [filteredItems]);

  const timetableItems: TimetableItem[] = useMemo(
    () =>
      filteredItems
        .map((item) => communicationCalendarToTimetableItem(item))
        .filter((item): item is TimetableItem => !!item),
    [filteredItems],
  );

  const onRangeChange = useCallback((start: string, end: string) => {
    setRangeStart(start);
    setRangeEnd(end);
    setMonthCursor(new Date(start));
  }, []);

  const setTab = useCallback(
    (next: "calendar" | "list") => {
      navigate({
        search: { tab: next },
        replace: true,
      });
    },
    [navigate],
  );

  const focusOnCalendar = useCallback(
    (item: CommunicationCalendarItemDTO) => {
      const raw = item.type === "event" ? item.startAt : item.dueDate;
      const part = isoToLocalYmd(raw);
      if (part) {
        setMonthCursor(new Date(`${part}T12:00:00`));
        setCalendarMountKey((k) => k + 1);
      }
      navigate({
        search: { tab: "calendar" },
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-start justify-between gap-3">
          <div data-tourid="communication-calendar-title">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">ປະຕິທິນວຽກ</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດລອງການນຳທາງ"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ວຽກທີ່ມອບຫມາຍ ແລະ ຕາຕະລາງການນັດຫມາຍຂອງທ່ານ.
            </p>
            <p className="mt-1 text-muted-foreground text-xs">
              ຊ່ວງສະແດງ: {rangeStart} – {rangeEnd}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: "/app/dashboard" })}
          >
            <ArrowLeft className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div>
          <div className="flex flex-col gap-4 border-border border-b py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ຄົ້ນຫາຕາຕະລາງ..."
              />
            </div>
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v as "calendar" | "list")}
              className="w-full sm:w-auto"
            >
              <TabsList className="grid w-full max-w-md grid-cols-2 sm:w-[min(100%,20rem)]">
                <TabsTrigger value="calendar">ປະຕິທິນ</TabsTrigger>
                <TabsTrigger value="list">ລາຍການ</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {tab === "calendar" ? (
            <div
              data-tourid="communication-calendar-list"
              className="space-y-3 py-4"
            >
              {isLoading ? (
                Array.from(
                  { length: 6 },
                  (_, i) => `calendar-skeleton-${i}`,
                ).map((key) => (
                  <div
                    key={key}
                    className="h-24 animate-pulse rounded-md bg-muted/60"
                  />
                ))
              ) : (
                <PublicTimetable
                  key={calendarMountKey}
                  items={timetableItems}
                  rooms={[]}
                  onSlotSelect={() => {}}
                  onRangeChange={onRangeChange}
                  initialView="month"
                  initialDate={monthCursor}
                />
              )}
            </div>
          ) : (
            <div
              data-tourid="communication-calendar-agenda"
              className="divide-y divide-border py-4"
            >
              {isLoading ? (
                Array.from({ length: 6 }, (_, i) => `agenda-skeleton-${i}`).map(
                  (key) => (
                    <div
                      key={key}
                      className="h-24 animate-pulse rounded-md bg-muted/60 py-4"
                    />
                  ),
                )
              ) : items.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  ບໍ່ມີຂໍ້ມູນປະຕິທິນ.
                </p>
              ) : sortedListItems.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  ບໍ່ພົບຂໍ້ມູນຕາມຄຳຄົ້ນຫາ.
                </p>
              ) : (
                sortedListItems.map((item) => (
                  <CalendarAgendaRow
                    key={`${item.type}-${item.id}`}
                    title={item.title}
                    kindLabel={kindLabel(item)}
                    whenLabel={formatAgendaWhen(item)}
                    onFocusCalendar={() => focusOnCalendar(item)}
                  />
                ))
              )}
            </div>
          )}
        </div>

        <CommunicationTour
          steps={calendarTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
